'use client'

import type { SocialProvider } from '@/lib/apis/types'
import {
  SOCIAL_AUTHENTICATION_ACKNOWLEDGEMENT,
  SOCIAL_AUTHENTICATION_MESSAGE,
  isExpectedSocialAuthenticationMessage,
  isExpectedSocialAuthenticationPayload,
  socialAuthenticationChannelName,
  type SocialAuthenticationErrorCode,
  type SocialAuthenticationResult,
} from '@/lib/auth/social-auth'
import { refreshRootApiClientAccessToken } from '@/lib/apis/clients/ApiClientStore'
import { requestSocialAuthenticationStartPath } from '@/lib/auth/social-auth-preparation-client'
import { useSession } from 'next-auth/react'
import { useCallback, useState } from 'react'

type StartSocialAuthenticationOptions = {
  popup?: boolean
  returnTo?: string
  errorTo?: string
  continuationToken?: string
  challenge?: {
    id: string
    token: string
  }
}

type UseSocialAuthenticationOptions = {
  onAuthenticated?: () => Promise<void> | void
}

type SocialAuthenticationMessage = {
  type: typeof SOCIAL_AUTHENTICATION_MESSAGE
  nonce: string
  result: SocialAuthenticationResult
}

function showPopupLoadingState(popup: Window) {
  try {
    const document = popup.document
    document.title = 'NextClinic sign-in'

    const viewport = document.createElement('meta')
    viewport.name = 'viewport'
    viewport.content = 'width=device-width, initial-scale=1'

    const styles = document.createElement('style')
    styles.textContent = `
      :root { color-scheme: light; }
      * { box-sizing: border-box; }
      body {
        margin: 0;
        background: #ffffff;
        color: #5c5c64;
        font-family: Arial, sans-serif;
      }
      main {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 16px;
        padding: 24px;
        text-align: center;
      }
      .spinner {
        width: 28px;
        height: 28px;
        border: 3px solid #d7d7dc;
        border-top-color: #2563eb;
        border-radius: 9999px;
        animation: nextclinic-spin 800ms linear infinite;
      }
      p { margin: 0; font-size: 14px; line-height: 20px; }
      @keyframes nextclinic-spin { to { transform: rotate(360deg); } }
      @media (prefers-reduced-motion: reduce) {
        .spinner { animation-duration: 1600ms; }
      }
    `

    const main = document.createElement('main')
    const spinner = document.createElement('div')
    spinner.className = 'spinner'
    spinner.setAttribute('aria-hidden', 'true')
    const status = document.createElement('p')
    status.setAttribute('aria-live', 'polite')
    status.setAttribute('role', 'status')
    status.textContent = 'Opening secure sign-in…'
    main.append(spinner, status)

    document.head.replaceChildren(viewport, styles)
    document.body.replaceChildren(main)
  } catch {
    // Loading feedback must never prevent the popup from reaching Auth0.
  }
}

function popupResult(
  popup: Window,
  nonce: string,
): Promise<SocialAuthenticationResult> {
  return new Promise((resolve) => {
    const channel =
      typeof BroadcastChannel === 'undefined'
        ? null
        : new BroadcastChannel(socialAuthenticationChannelName(nonce))
    let cancellationTimer: number | undefined
    let expiryTimer: number | undefined
    let settled = false

    const cleanup = () => {
      window.removeEventListener('message', onMessage)
      window.removeEventListener('focus', onWindowFocus)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      channel?.removeEventListener('message', onChannelMessage)
      channel?.close()
      if (cancellationTimer) window.clearTimeout(cancellationTimer)
      if (expiryTimer) window.clearTimeout(expiryTimer)
    }

    const finish = (result: SocialAuthenticationResult) => {
      if (settled) return
      settled = true
      cleanup()
      resolve(result)
    }

    const finishAsCancelledIfClosed = () => {
      if (settled || !popup.closed) return
      finish({
        ok: false,
        nonce,
        mode: 'authenticate',
        provider: 'APPLE',
        error: 'SOCIAL_AUTHENTICATION_CANCELLED',
      })
    }

    const scheduleCancellationCheck = () => {
      if (settled || document.visibilityState !== 'visible') return
      if (cancellationTimer) window.clearTimeout(cancellationTimer)
      cancellationTimer = window.setTimeout(finishAsCancelledIfClosed, 750)
    }

    const onWindowFocus = () => scheduleCancellationCheck()

    const onVisibilityChange = () => scheduleCancellationCheck()

    const onMessage = (event: MessageEvent<SocialAuthenticationMessage>) => {
      if (
        !isExpectedSocialAuthenticationMessage(
          event,
          popup,
          window.location.origin,
          nonce,
        )
      ) {
        return
      }
      finish(event.data.result)
      popup.close()
    }

    const onChannelMessage = (
      event: MessageEvent<SocialAuthenticationMessage>,
    ) => {
      if (!isExpectedSocialAuthenticationPayload(event.data, nonce)) return

      channel?.postMessage({
        type: SOCIAL_AUTHENTICATION_ACKNOWLEDGEMENT,
        nonce,
      })
      finish(event.data.result)
      popup.close()
    }

    window.addEventListener('message', onMessage)
    window.addEventListener('focus', onWindowFocus)
    document.addEventListener('visibilitychange', onVisibilityChange)
    channel?.addEventListener('message', onChannelMessage)
    expiryTimer = window.setTimeout(
      () => {
        popup.close()
        finish({
          ok: false,
          nonce,
          mode: 'authenticate',
          provider: 'APPLE',
          error: 'SOCIAL_AUTHENTICATION_FAILED',
        })
      },
      10 * 60 * 1000,
    )
  })
}

export default function useSocialAuthentication({
  onAuthenticated,
}: UseSocialAuthenticationOptions = {}) {
  const { update } = useSession()
  const [pendingProvider, setPendingProvider] = useState<SocialProvider | null>(
    null,
  )
  const [error, setError] = useState<SocialAuthenticationErrorCode | null>(null)

  const start = useCallback(
    async (
      provider: SocialProvider,
      options: StartSocialAuthenticationOptions = {},
    ): Promise<SocialAuthenticationResult | null> => {
      const popupMode = options.popup === true
      const nonce = crypto.randomUUID()
      let popup: Window | null = null

      setPendingProvider(provider)
      setError(null)

      if (popupMode) {
        popup = window.open(
          'about:blank',
          `nextclinic-social-${nonce}`,
          'popup=yes,width=520,height=720,resizable=yes,scrollbars=yes',
        )
        if (!popup) {
          setPendingProvider(null)
          setError('POPUP_BLOCKED')
          return {
            ok: false,
            nonce,
            mode: options.challenge ? 'challenge' : 'authenticate',
            provider,
            error: 'POPUP_BLOCKED',
          }
        }
        showPopupLoadingState(popup)
      }

      try {
        const startPath = await requestSocialAuthenticationStartPath({
          nonce,
          provider,
          popup: popupMode,
          returnTo: options.returnTo ?? '/portal',
          errorTo: options.errorTo,
          continuationToken: options.continuationToken,
          challenge: options.challenge,
        })

        if (!popupMode) {
          window.location.assign(startPath)
          return null
        }

        const resultPromise = popupResult(popup!, nonce)
        popup!.location.assign(startPath)
        popup!.focus()
        const result = await resultPromise

        // Fill in the expected flow details rather than trusting values from a
        // cross-window message, even though origin/source/nonce were checked.
        const normalizedResult: SocialAuthenticationResult = {
          ...result,
          nonce,
          provider,
          mode: options.challenge ? 'challenge' : 'authenticate',
        }

        if (!normalizedResult.ok) {
          setError(normalizedResult.error ?? 'SOCIAL_AUTHENTICATION_FAILED')
          return normalizedResult
        }

        if (!options.challenge) {
          await update()
          await refreshRootApiClientAccessToken()
          await onAuthenticated?.()
        }

        return normalizedResult
      } catch {
        popup?.close()
        const result: SocialAuthenticationResult = {
          ok: false,
          nonce,
          mode: options.challenge ? 'challenge' : 'authenticate',
          provider,
          error: 'SOCIAL_AUTHENTICATION_FAILED',
        }
        setError(result.error!)
        return result
      } finally {
        setPendingProvider(null)
      }
    },
    [onAuthenticated, update],
  )

  return {
    error,
    pendingProvider,
    setError,
    start,
  }
}
